import { Table, ActionIcon, Menu, Badge } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDotsVertical, IconEdit, IconRefresh, IconTrash } from '@tabler/icons-react';

import { CustomTable, CustomTableMobile, useResponsive } from '@/shared';
import { useDeleteConversionPair } from '../hooks/use-delete-conversion-pair';
import { useRefreshConversionPair } from '../hooks/use-refresh-conversion-pair';
import { EditConversionPairForm } from './edit-conversion-pair-form';
import { getConversionPairTitle } from '../utils/helpers/converter';

import type { TConversionPair } from '../store/types';

type Props = {
  items: TConversionPair[];
  isLoading?: boolean;
};

export const ConversionPairsTable = (props: Props) => {
  const { items } = props;
  const { isDesktop } = useResponsive();
  const { isLoading: isRefreshLoading, handleRefresh } = useRefreshConversionPair();
  const { openDeleteModal } = useDeleteConversionPair();

  const openEditModal = (item: TConversionPair) => modals.open({
    title: `Редактирование пары ${getConversionPairTitle(item)}`,
    centered: true,
    children: (
      <EditConversionPairForm
        conversionPair={item}
        onClose={modals.closeAll}
      />
    ),
  });

  const getRefreshMode = (item: TConversionPair) => (
    <Badge color={item.autoRefresh ? 'green' : 'gray'} variant="light">
      {item.autoRefresh ? 'Авто' : 'Вручную'}
    </Badge>
  );

  const getActions = (item: TConversionPair) => (
    <Menu position="bottom-end" shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="subtle">
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconRefresh size={14} />}
          disabled={isRefreshLoading}
          onClick={() => handleRefresh(item)}
        >
          Обновить курс
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconEdit size={14} />}
          onClick={() => openEditModal(item)}
        >
          Редактировать
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() => openDeleteModal(item)}
        >
          Удалить
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const titles = [
    'ID',
    'Торговая пара',
    'Курс',
    'Наценка (%)',
    'Курс конвертации',
    'Обновление',
    'Курс обновлён',
  ];

  const rows = items.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.id}</Table.Td>
      <Table.Td>{getConversionPairTitle(item)}</Table.Td>
      <Table.Td>{item.rate}</Table.Td>
      <Table.Td>{item.markupPercent}</Table.Td>
      <Table.Td>{item.conversionRate}</Table.Td>
      <Table.Td>{getRefreshMode(item)}</Table.Td>
      <Table.Td>{item.rateUpdatedAt}</Table.Td>
      <Table.Td width={50}>{getActions(item)}</Table.Td>
    </Table.Tr>
  ));

  const rowsMobile = items.map((item) => ({
    key: item.id,
    headerActions: getActions(item),
    items: [
      { name: titles[1], value: getConversionPairTitle(item) },
      { name: titles[4], value: item.conversionRate },
      { name: titles[5], value: getRefreshMode(item) },
      { name: titles[0], value: item.id },
      { name: titles[2], value: item.rate },
      { name: titles[3], value: item.markupPercent },
      { name: titles[6], value: item.rateUpdatedAt },
    ],
  }));

  return isDesktop
    ? (
      <CustomTable
        {...props}
        titles={titles}
        rows={rows}
        isPaginated={false}
      />
    )
    : (
      <CustomTableMobile
        rows={rowsMobile}
        headerPointsCount={3}
        isPaginated={false}
        {...props}
      />
    );
};
